import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo } from 'react';
import type { Location } from 'react-router';
import { useLocation, useSearchParams } from 'react-router';

import type { UrlTileDefinition, UrlTileLegacyRewriter } from './types';
import { URL_TILE_SEARCH_PARAM } from './types';

const rewriteLegacySearchParams = ({
    definitions,
    legacyRewriters,
    location,
    searchParams,
}: {
    readonly definitions: readonly UrlTileDefinition[];
    readonly legacyRewriters: readonly UrlTileLegacyRewriter[];
    readonly location: Location;
    readonly searchParams: URLSearchParams;
}): URLSearchParams | undefined => {
    const hadCanonicalTileParams = searchParams.has(URL_TILE_SEARCH_PARAM);
    let didRewrite = false;
    let nextParams = searchParams;

    for (const legacyRewriter of legacyRewriters) {
        const rewrittenParams = legacyRewriter.rewrite({
            definitions,
            hadCanonicalTileParams,
            location,
            searchParams: nextParams,
        });

        if (is.undefined(rewrittenParams)) {
            continue;
        }

        didRewrite = true;
        nextParams = rewrittenParams;
    }

    return didRewrite ? nextParams : undefined;
};

type UrlTileLegacyRedirectProps = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly legacyRewriters: readonly UrlTileLegacyRewriter[];
};

export const UrlTileLegacyRedirect: FunctionComponent<UrlTileLegacyRedirectProps> = ({
    definitions,
    legacyRewriters,
}) => {
    const location = useLocation();
    const [, setSearchParams] = useSearchParams();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    useEffect(() => {
        const nextParams = rewriteLegacySearchParams({
            definitions,
            legacyRewriters,
            location,
            searchParams,
        });

        if (is.undefined(nextParams) || nextParams.toString() === searchParams.toString()) {
            return;
        }

        // Deferred a tick: effects fire child-first, so on mount this would run
        // before the parent nuqs adapter/provider subscribe to the history-sync
        // emitter. An immediate rewrite would be emitted with no listeners and
        // tiles would render from the stale pre-rewrite state.
        const timeoutId = globalThis.setTimeout(() => {
            setSearchParams(nextParams, { replace: true });
        }, 0);

        return () => globalThis.clearTimeout(timeoutId);
    }, [definitions, legacyRewriters, location, searchParams, setSearchParams]);

    return null;
};
